/*
 * product-modal-zoom.js
 * ---------------------------------------------------------------------------
 * Turns Dawn's product lightbox (product-media-modal) into a proper full-screen
 * image viewer on every device:
 *   - one image at a time, fitted to the screen
 *   - double-tap / double-click to zoom in and out (centred on the point)
 *   - pinch (touch) or scroll-wheel (mouse) to zoom continuously
 *   - drag to pan, clamped so the image never escapes the frame
 *   - prev / next arrows and Left/Right keys to move between photos
 *
 * Out of the box Dawn drops the image in at full pixel size (it overflows on a
 * phone) and stacks every photo in a scrolling column on desktop. This replaces
 * that with a fit-then-zoom viewer shared by desktop and mobile.
 *
 * Progressive enhancement: the paired CSS already fits the image to the screen.
 * This script adds the gestures and navigation. If it fails to load, the image
 * still fits and the browser's native pinch-zoom remains available on touch.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (!('PointerEvent' in window)) return;

  var MAX_SCALE = 4; // furthest a customer can zoom in
  var DOUBLE_SCALE = 2.5; // zoom level a double-tap / double-click jumps to
  var DOUBLE_MS = 300; // max gap between taps to count as a double-tap
  var DOUBLE_SLOP = 30; // max pointer travel (px) between taps
  var PAN_SLOP = 4; // movement (px) before a press counts as a drag, not a tap
  var WHEEL_STEP = 1.15; // zoom factor per wheel notch

  function clamp(value, min, max) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  /* Manages the transform (scale + pan) of a single lightbox image. */
  function ZoomableImage(img, container) {
    this.img = img;
    this.container = container;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;

    // Base (fit-to-screen) geometry, measured fresh at the start of a gesture.
    this.originX = 0;
    this.originY = 0;
    this.baseW = 0;
    this.baseH = 0;

    this.pointers = new Map();
    this.pinch = null;
    this.pan = null;
    this.lastTapTime = 0;
    this.lastTapX = 0;
    this.lastTapY = 0;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onWheel = this.onWheel.bind(this);

    img.classList.add('pz-active'); // CSS uses this to set touch-action: none
    img.addEventListener('pointerdown', this.onDown);
    img.addEventListener('pointermove', this.onMove);
    img.addEventListener('pointerup', this.onUp);
    img.addEventListener('pointercancel', this.onUp);
    img.addEventListener('wheel', this.onWheel, { passive: false });
    img.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  }

  /* Record the image's fit-to-screen position/size. Only valid at scale 1. */
  ZoomableImage.prototype.measure = function () {
    var previous = this.img.style.transform;
    this.img.style.transform = 'none';
    var rect = this.img.getBoundingClientRect();
    this.originX = rect.left;
    this.originY = rect.top;
    this.baseW = rect.width;
    this.baseH = rect.height;
    this.img.style.transform = previous;
  };

  ZoomableImage.prototype.reset = function () {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.pointers.clear();
    this.pinch = null;
    this.pan = null;
    this.img.style.transform = '';
    this.img.classList.remove('is-zoomed');
    this.container.classList.remove('pz-is-zoomed');
  };

  ZoomableImage.prototype.apply = function () {
    if (this.scale <= 1) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.img.style.transform = '';
      this.img.classList.remove('is-zoomed');
      this.container.classList.remove('pz-is-zoomed');
      return;
    }
    this.clampPan();
    this.img.style.transform =
      'translate(' + this.tx + 'px, ' + this.ty + 'px) scale(' + this.scale + ')';
    this.img.classList.add('is-zoomed');
    this.container.classList.add('pz-is-zoomed');
  };

  /* Keep the (zoomed) image covering the frame and never sliding off-screen. */
  ZoomableImage.prototype.clampPan = function () {
    var containerRect = this.container.getBoundingClientRect();
    var cw = containerRect.width;
    var ch = containerRect.height;
    var w = this.baseW * this.scale;
    var h = this.baseH * this.scale;
    var ox = this.originX - containerRect.left;
    var oy = this.originY - containerRect.top;

    if (w <= cw) {
      this.tx = (cw - w) / 2 - ox;
    } else {
      this.tx = clamp(this.tx, cw - w - ox, -ox);
    }

    if (h <= ch) {
      this.ty = (ch - h) / 2 - oy;
    } else {
      this.ty = clamp(this.ty, ch - h - oy, -oy);
    }
  };

  /* Zoom to `newScale` keeping the screen point (fx, fy) under the finger/cursor. */
  ZoomableImage.prototype.zoomTo = function (newScale, fx, fy) {
    newScale = clamp(newScale, 1, MAX_SCALE);
    var gx = fx - this.originX;
    var gy = fy - this.originY;
    var localX = (gx - this.tx) / this.scale;
    var localY = (gy - this.ty) / this.scale;
    this.tx = gx - newScale * localX;
    this.ty = gy - newScale * localY;
    this.scale = newScale;
    this.apply();
  };

  ZoomableImage.prototype.onDown = function (event) {
    if (this.scale === 1) this.measure();
    if (event.pointerType === 'mouse') event.preventDefault();

    if (this.img.setPointerCapture) {
      try {
        this.img.setPointerCapture(event.pointerId);
      } catch (e) {
        /* ignore */
      }
    }

    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size === 2) {
      var pts = Array.from(this.pointers.values());
      var dx = pts[0].x - pts[1].x;
      var dy = pts[0].y - pts[1].y;
      this.pinch = { startDist: Math.hypot(dx, dy) || 1, startScale: this.scale };
      this.pan = null;
    } else if (this.pointers.size === 1) {
      this.pan = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty, moved: false };
    }
  };

  ZoomableImage.prototype.onMove = function (event) {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.pointers.size >= 2 && this.pinch) {
      event.preventDefault();
      var pts = Array.from(this.pointers.values());
      var dx = pts[0].x - pts[1].x;
      var dy = pts[0].y - pts[1].y;
      var dist = Math.hypot(dx, dy);
      var midX = (pts[0].x + pts[1].x) / 2;
      var midY = (pts[0].y + pts[1].y) / 2;
      this.zoomTo(this.pinch.startScale * (dist / this.pinch.startDist), midX, midY);
    } else if (this.pointers.size === 1 && this.pan && this.scale > 1) {
      event.preventDefault();
      var p = this.pointers.values().next().value;
      var ddx = p.x - this.pan.x;
      var ddy = p.y - this.pan.y;
      if (Math.abs(ddx) + Math.abs(ddy) > PAN_SLOP) this.pan.moved = true;
      this.tx = this.pan.tx + ddx;
      this.ty = this.pan.ty + ddy;
      this.apply();
    }
  };

  ZoomableImage.prototype.onUp = function (event) {
    // Dawn's media-modal closes on any mouse pointerup that isn't on a
    // video/3D model. Keep clicks on the image for zoom/pan, not closing —
    // clicks on the backdrop still bubble up and close the modal as normal.
    event.stopPropagation();

    var hadPointer = this.pointers.has(event.pointerId);
    this.pointers.delete(event.pointerId);

    if (this.pointers.size < 2) this.pinch = null;

    if (this.pointers.size === 1) {
      // One pointer left after a pinch — hand control back to panning.
      var p = this.pointers.values().next().value;
      this.pan = { x: p.x, y: p.y, tx: this.tx, ty: this.ty, moved: true };
      return;
    }

    if (this.pointers.size === 0) {
      var moved = this.pan && this.pan.moved;
      this.pan = null;
      if (moved || !hadPointer) return;

      // A clean tap / click — check whether it completes a double.
      var now = Date.now();
      var near =
        Math.abs(event.clientX - this.lastTapX) < DOUBLE_SLOP &&
        Math.abs(event.clientY - this.lastTapY) < DOUBLE_SLOP;

      if (now - this.lastTapTime < DOUBLE_MS && near) {
        if (this.scale > 1) {
          this.reset();
        } else {
          this.measure();
          this.zoomTo(DOUBLE_SCALE, event.clientX, event.clientY);
        }
        this.lastTapTime = 0;
      } else {
        this.lastTapTime = now;
        this.lastTapX = event.clientX;
        this.lastTapY = event.clientY;
      }
    }
  };

  ZoomableImage.prototype.onWheel = function (event) {
    event.preventDefault();
    if (this.scale === 1) this.measure();
    var factor = event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
    this.zoomTo(this.scale * factor, event.clientX, event.clientY);
  };

  /* Owns one product-modal: arrows, keyboard nav, and the per-image zoomers. */
  function ModalController(modal) {
    this.modal = modal;
    this.content = modal.querySelector('.product-media-modal__content');
    if (!this.content) return;

    this.media = Array.from(this.content.children);
    this.zoomables = [];
    this.index = 0;

    var self = this;
    this.media.forEach(function (el) {
      if (el.tagName === 'IMG') {
        self.zoomables.push(new ZoomableImage(el, self.content));
      }
    });

    // The lightbox images are loading="lazy" and hidden until opened, so each
    // photo only downloads when you open/navigate to it. Preload the modal-res
    // image on intent (gallery hover / touch-down), plus the active image and
    // its neighbours, so opening and the arrows feel instant.
    this.imgById = {};
    this.media.forEach(function (el) {
      if (el.tagName === 'IMG') {
        var id = el.getAttribute('data-media-id');
        if (id) self.imgById[id] = el;
      }
    });

    var openerSelector =
      'modal-opener[data-modal="#' + this.modal.id + '"] .product__media-toggle[data-media-id]';
    document.querySelectorAll(openerSelector).forEach(function (toggle) {
      var pre = function () {
        self.preload(self.imgById[toggle.getAttribute('data-media-id')]);
      };
      toggle.addEventListener('pointerenter', pre);
      toggle.addEventListener('pointerdown', pre);
    });

    // Warm the most-likely-first photo (the featured image) once the page is idle.
    var preloadFeatured = function () {
      self.preload(self.media[0]);
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(preloadFeatured, { timeout: 2500 });
    } else {
      window.setTimeout(preloadFeatured, 1500);
    }

    // Stop the arrows' pointerup from reaching Dawn's "click closes the modal"
    // handler, otherwise navigating would dismiss the lightbox.
    var stop = function (event) {
      event.stopPropagation();
    };

    this.prevBtn = modal.querySelector('.pz-nav--prev');
    this.nextBtn = modal.querySelector('.pz-nav--next');
    if (this.prevBtn) {
      this.prevBtn.addEventListener('pointerup', stop);
      this.prevBtn.addEventListener('click', this.prev.bind(this));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('pointerup', stop);
      this.nextBtn.addEventListener('click', this.next.bind(this));
    }

    this.onKey = this.onKey.bind(this);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName !== 'open') return;
        if (modal.hasAttribute('open')) self.onOpen();
        else self.onClose();
      });
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });

    window.addEventListener('resize', function () {
      self.resetZoom();
    });
  }

  ModalController.prototype.resetZoom = function () {
    this.zoomables.forEach(function (zoomable) {
      zoomable.reset();
    });
  };

  ModalController.prototype.preload = function (el) {
    if (el && el.tagName === 'IMG' && el.loading === 'lazy') {
      el.loading = 'eager';
    }
  };

  ModalController.prototype.preloadAround = function (index) {
    var n = this.media.length;
    if (!n) return;
    var self = this;
    [index, index + 1, index - 1].forEach(function (i) {
      self.preload(self.media[((i % n) + n) % n]);
    });
  };

  ModalController.prototype.onOpen = function () {
    var self = this;
    requestAnimationFrame(function () {
      if (!self.modal.hasAttribute('open')) return;
      self.resetZoom();
      var activeEl = self.content.querySelector('.active');
      var idx = activeEl ? self.media.indexOf(activeEl) : 0;
      self.show(idx < 0 ? 0 : idx);
      document.addEventListener('keydown', self.onKey);
    });
  };

  ModalController.prototype.onClose = function () {
    this.resetZoom();
    document.removeEventListener('keydown', this.onKey);
  };

  ModalController.prototype.show = function (index) {
    if (!this.media.length) return;
    this.resetZoom();
    this.index = (index + this.media.length) % this.media.length;
    var current = this.index;
    this.media.forEach(function (el, i) {
      el.classList.toggle('active', i === current);
    });
    this.preloadAround(current);
  };

  ModalController.prototype.next = function (event) {
    if (event) event.preventDefault();
    this.show(this.index + 1);
  };

  ModalController.prototype.prev = function (event) {
    if (event) event.preventDefault();
    this.show(this.index - 1);
  };

  ModalController.prototype.onKey = function (event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  };

  function setup() {
    document.querySelectorAll('product-modal').forEach(function (modal) {
      new ModalController(modal);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
